import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search, X, Filter, Loader2, Info, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { STATE_NAMES } from '../data/usStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEST_POLICY_LABELS = {
  A: 'Always required',
  F: 'Test flexible',
  I: 'Tests ignored',
  N: 'Never required',
  S: 'Sometimes required',
};

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-all ${
        active
          ? 'bg-[#0F392B] text-white border-[#0F392B] shadow-sm'
          : 'bg-white text-[#0F392B] border-[#E2E2E2] hover:border-[#0F392B]/40'
      }`}
    >
      {children}
    </button>
  );
}

function Cell({ value, muted = false }) {
  if (!value || value === 'None') return <span className="text-[#C9C9C0]">—</span>;
  if (value === 'See Website') return <span className="text-[11px] text-muted-foreground italic">See site</span>;
  return <span className={`text-[12px] ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{value}</span>;
}

export default function DeadlinesGridPage() {
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebounced(rawQuery, 300);
  const [testPolicy, setTestPolicy] = useState(null);
  const [region, setRegion] = useState(null);
  const [plan, setPlan] = useState(null); // 'ed' | 'ea' | 'rea' | 'rolling'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [skip, setSkip] = useState(0);
  const LIMIT = 60;

  useEffect(() => {
    fetch(`${API_URL}/api/common-app/requirements-stats`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
    fetch(`${API_URL}/api/common-app/states`)
      .then((r) => r.json())
      .then((d) => setStatesList(d.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSkip(0);
  }, [query, testPolicy, plan, region]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (testPolicy) params.set('test_policy', testPolicy);
    if (plan === 'ed') params.set('has_ed', 'true');
    if (plan === 'ea') params.set('has_ea', 'true');
    if (plan === 'rea') params.set('has_rea', 'true');
    if (plan === 'rolling') params.set('has_rolling', 'true');
    if (region) params.set('region', region);
    params.set('limit', LIMIT);
    params.set('skip', skip);

    setLoading(true);
    fetch(`${API_URL}/api/common-app/requirements?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [query, testPolicy, plan, region, skip]);

  const anyFilter = !!(query || testPolicy || plan || region);
  const reset = () => { setRawQuery(''); setTestPolicy(null); setPlan(null); setRegion(null); };
  const showingRange = useMemo(
    () => (total === 0 ? '0' : `${skip + 1}–${Math.min(skip + items.length, total)}`),
    [skip, items.length, total]
  );

  const schema = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Common Application 2025-26 First-Year Requirements Grid",
    "description": "Searchable database of 1,118 Common App member colleges with deadlines (ED, EA, REA, Rolling), application fees, testing policies, and recommendation requirements for the 2025-26 admissions cycle.",
    "creator": { "@type": "EducationalOrganization", "name": "Ivy League Consulting" },
    "temporalCoverage": "2025-2026",
    "dateModified": "2025-10-24",
    "keywords": ["common app deadlines", "college application fees", "test policy", "early decision", "early action"],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="deadlines-grid-page">
      <SEO
        title="Common App Deadlines & Fees 2025-26 — Searchable Grid for 1,000+ Colleges"
        description="Search 1,118 Common App member colleges by name. Compare Early Decision, Early Action, REA, and Rolling deadlines, application fees, testing policies, and recommendation requirements for the 2025-26 cycle."
        keywords="common app deadlines, common app fees, early decision deadlines 2025-26, ivy league early action, college application fees, test optional colleges 2026"
        schema={schema}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-20 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">Common App 2025-26</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Every Common App deadline,<br />
            <span className="italic text-[#D4AF37]">one searchable page.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-6">
            {stats ? `${stats.total.toLocaleString()} member colleges. ` : ''}
            Every ED, EA, REA, and Rolling deadline. Every application fee. Every test policy.
            Updated {stats?.updated || '10-24-2025'}.
          </p>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-8" data-testid="deadlines-stats">
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.has_ed}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Offer ED</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.has_ea}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Offer EA</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.has_rea}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Offer REA</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.by_test_policy?.A || 0}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Tests Always Req</p></div>
            </div>
          )}
        </div>
      </section>

      {/* Filter + Table */}
      <section className="py-12 md:py-16" data-testid="deadlines-directory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          <div className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-5 md:p-6 mb-8" data-testid="deadlines-filters">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1 flex items-center gap-1.5"><Filter className="h-3 w-3" /> Filter the grid</p>
                <h2 className="text-lg font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Find your school
                </h2>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={rawQuery}
                  onChange={(e) => setRawQuery(e.target.value)}
                  placeholder={stats ? `Search ${stats.total.toLocaleString()} colleges…` : 'Search colleges…'}
                  className="pl-9 h-11 bg-white border-[#E2E2E2] text-sm"
                  data-testid="deadlines-search"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">Offers plan</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={!plan} onClick={() => setPlan(null)} testid="plan-filter-all">All plans</Chip>
                  <Chip active={plan === 'ed'} onClick={() => setPlan('ed')} testid="plan-filter-ed">Early Decision</Chip>
                  <Chip active={plan === 'ea'} onClick={() => setPlan('ea')} testid="plan-filter-ea">Early Action</Chip>
                  <Chip active={plan === 'rea'} onClick={() => setPlan('rea')} testid="plan-filter-rea">REA</Chip>
                  <Chip active={plan === 'rolling'} onClick={() => setPlan('rolling')} testid="plan-filter-rolling">Rolling</Chip>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">Test policy</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={!testPolicy} onClick={() => setTestPolicy(null)} testid="test-filter-all">All policies</Chip>
                  {Object.entries(TEST_POLICY_LABELS).map(([k, label]) => (
                    <Chip key={k} active={testPolicy === k} onClick={() => setTestPolicy(k)} testid={`test-filter-${k.toLowerCase()}`}>
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">Region</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={!region} onClick={() => setRegion(null)} testid="region-filter-all">All regions</Chip>
                  {['Northeast','Midwest','South','West','International'].map((r) => (
                    <Chip key={r} active={region === r} onClick={() => setRegion(r)} testid={`region-filter-${r.toLowerCase()}`}>
                      {r}{stats?.by_region?.[r] ? <span className="ml-1 opacity-60">({stats.by_region[r]})</span> : null}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E2E2]">
              <p className="text-xs text-muted-foreground" data-testid="deadlines-count">
                Showing <span className="text-[#0F392B] font-semibold">{showingRange}</span> of {total.toLocaleString()} colleges
              </p>
              {anyFilter && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#0F392B] hover:text-[#0F392B]/70"
                  data-testid="deadlines-reset"
                >
                  <X className="h-3 w-3" /> Reset filters
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading colleges…
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16" data-testid="deadlines-empty">
                <p className="text-base text-[#0F392B] font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  No colleges match those filters.
                </p>
                <p className="text-sm text-muted-foreground mb-4">Try a broader search or reset your filters.</p>
                <Button onClick={reset} variant="outline" className="border-[#0F392B] text-[#0F392B]">Reset filters</Button>
              </div>
            ) : (
              <table className="w-full text-sm min-w-[900px]" data-testid="deadlines-table">
                <thead>
                  <tr className="border-b border-[#E2E2E2] bg-[#F9F9F7]">
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">College</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">ED</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EDII</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EA</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">REA</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">RD / Rolling</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Fee (US / INTL)</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" title="Test Policy">Tests</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" title="Teacher Evals">TE</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r.slug} className={`border-b border-[#E2E2E2] last:border-b-0 hover:bg-[#F9F9F7]/60 cursor-pointer ${i % 2 === 1 ? 'bg-[#F9F9F7]/30' : ''}`} data-testid={`deadlines-row-${r.slug}`} onClick={() => navigate(`/deadlines-grid/${r.slug}`)}>
                      <td className="py-3 px-4">
                        <p className="text-[13px] font-semibold text-[#0F392B] hover:underline" style={{ fontFamily: "'Playfair Display', serif" }}>{r.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{r.school_type || 'Coed'}</p>
                      </td>
                      <td className="py-3 px-3"><Cell value={r.ed} /></td>
                      <td className="py-3 px-3"><Cell value={r.edii} /></td>
                      <td className="py-3 px-3"><Cell value={r.ea} /></td>
                      <td className="py-3 px-3"><Cell value={r.rea} /></td>
                      <td className="py-3 px-3"><Cell value={r.rd_rolling} /></td>
                      <td className="py-3 px-3">
                        <p className="text-[12px] text-foreground">{r.fee_us || '—'}{r.fee_intl && r.fee_intl !== r.fee_us && <span className="text-muted-foreground"> / {r.fee_intl}</span>}</p>
                      </td>
                      <td className="py-3 px-3" title={TEST_POLICY_LABELS[r.test_policy] || ''}>
                        {r.test_policy ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                            r.test_policy === 'A' ? 'bg-[#0F392B] text-white' :
                            r.test_policy === 'N' || r.test_policy === 'I' ? 'bg-[#F9F9F7] text-[#0F392B] border border-[#E2E2E2]' :
                            'bg-[#D4AF37]/20 text-[#0F392B]'
                          }`}>{r.test_policy}</span>
                        ) : <span className="text-[#C9C9C0]">—</span>}
                      </td>
                      <td className="py-3 px-3"><Cell value={r.te} muted /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && total > LIMIT && (
            <div className="flex items-center justify-between mt-6" data-testid="deadlines-pagination">
              <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="deadlines-prev">
                Previous
              </Button>
              <p className="text-xs text-muted-foreground">{showingRange} of {total.toLocaleString()}</p>
              <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="deadlines-next">
                Next
              </Button>
            </div>
          )}

          {/* Legend */}
          <div className="mt-10 bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-6" data-testid="deadlines-legend">
            <div className="flex items-start gap-2 mb-3">
              <Info className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-[#0F392B] uppercase tracking-widest">How to read the grid</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px] text-muted-foreground leading-relaxed">
              <p><span className="text-foreground font-semibold">Test policy:</span> A = Always required, F = Flexible, I = Ignored, N = Never required, S = Sometimes required.</p>
              <p><span className="text-foreground font-semibold">TE:</span> Teacher evaluations required. Blank means none required per the Common App grid.</p>
              <p><span className="text-foreground font-semibold">See site:</span> Common App notes that this policy is too complex to summarize — check the college directly.</p>
              <p><span className="text-foreground font-semibold">Source:</span> Common Application 2025-26 first-year grid, published Oct 24, 2025. Always verify with each college before submitting.</p>
            </div>
          </div>

          {/* Popular lists */}
          <div className="mt-10" data-testid="deadlines-popular-lists">
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1">Curated lists</p>
              <h2 className="text-lg font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Popular lists from this grid
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { to: '/deadlines-grid/no-application-fee', label: 'No application fee', sub: '590+ free-to-apply colleges' },
                { to: '/deadlines-grid/test-optional', label: 'Test-optional', sub: '580 schools, SAT/ACT never required' },
                { to: '/deadlines-grid/rolling-admissions', label: 'Rolling admissions', sub: '600+ schools, decisions in weeks' },
                { to: '/deadlines-grid/early-action', label: 'Early Action', sub: '446 schools with EA deadlines' },
                { to: '/deadlines-grid/early-decision-2', label: 'Early Decision II', sub: '122 schools with a January round' },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="block bg-white border border-[#E2E2E2] rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`popular-list-${l.to.split('/').pop()}`}>
                  <p className="text-sm font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{l.label} →</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{l.sub}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* Browse by state */}
          {statesList.length > 0 && (
            <div className="mt-10" data-testid="deadlines-browse-by-state">
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1">Regional guides</p>
                <h2 className="text-lg font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Browse by state
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Every deadline, fee, and test policy for Common App colleges in your state.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {statesList.map((s) => (
                  <Link
                    key={s.state}
                    to={`/deadlines-grid/state/${s.state.toLowerCase()}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-white text-[#0F392B] border border-[#E2E2E2] hover:border-[#0F392B]/40 transition-all"
                    data-testid={`grid-state-link-${s.state.toLowerCase()}`}
                  >
                    {STATE_NAMES[s.state] || s.state} <span className="opacity-60">({s.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="deadlines-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Turn deadlines into a managed strategy.
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            The Ivy League Admissions Portal auto-tracks every deadline on your list — with recommender status, essay drafts, and school priorities in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="deadlines-cta-platform">
                Start the Platform — $99 <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/diagnostic">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="deadlines-cta-diagnostic">
                Take the Diagnostic <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
